import { Link } from 'react-router-dom';
import React, { useEffect, useState, useRef } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { Header } from './Header';
import { Footer } from './Footer';
import { RelatedLocations } from './RelatedLocations';
import { LocationFAQ } from './LocationFAQ';
import { SEO } from './SEO';
import { 
  Server, Zap, Shield, Globe, 
  CheckCircle, HardDrive, Network,
  Clock, MapPin, TrendingUp
} from 'lucide-react';
import { Button } from './ui/button';
import { PricingSection } from './PricingSection';
import { PaymentMethodsSection } from './PaymentMethodsSection';
import { smoothScrollToWithOffset } from '../lib/scroll';

export const NetherlandsPage = () => {
  const { t, language } = useLanguage();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const stats = [
    { icon: Clock, value: t('netherlands.stats.uptime'), label: t('netherlands.stats.uptimeLabel') },
    { icon: Zap, value: t('netherlands.stats.speed'), label: t('netherlands.stats.speedLabel'), wiki: 'shared-1gbps-port-explained' },
    { icon: TrendingUp, value: t('netherlands.stats.traffic'), label: t('netherlands.stats.trafficLabel') },
    { icon: MapPin, value: t('netherlands.stats.location'), label: t('netherlands.stats.locationLabel') }
  ];

  const benefits = [
    {
      icon: Shield,
      title: t('netherlands.why.privacy.title'),
      description: t('netherlands.why.privacy.desc')
    },
    {
      icon: Server,
      title: t('netherlands.why.infrastructure.title'),
      description: t('netherlands.why.infrastructure.desc')
    },
    {
      icon: Zap,
      title: t('netherlands.why.speed.title'),
      description: t('netherlands.why.speed.desc')
    },
    {
      icon: Globe,
      title: t('netherlands.why.location.title'),
      description: t('netherlands.why.location.desc')
    }
  ];

  const features = [
    {
      icon: HardDrive,
      title: t('netherlands.features.nvme.title'),
      description: t('netherlands.features.nvme.desc')
    },
    {
      icon: Network,
      title: t('netherlands.features.network.title'),
      description: t('netherlands.features.network.desc')
    },
    {
      icon: TrendingUp,
      title: t('netherlands.features.traffic.title'),
      description: t('netherlands.features.traffic.desc')
    },
    {
      icon: CheckCircle,
      title: t('netherlands.features.uptime.title'),
      description: t('netherlands.features.uptime.desc')
    }
  ];

  const scrollToPricing = () => smoothScrollToWithOffset('pricing', 100);

  return (
    <div className="min-h-screen bg-white">
      <SEO page="netherlands" />
      <Header />
      
      {/* Hero Section */}
      <section className="relative py-20 bg-gradient-to-br from-red-900 via-red-800 to-red-900 overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-20 left-10 w-72 h-72 bg-red-500 rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-20 w-96 h-96 bg-red-400 rounded-full blur-3xl"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center mb-12">
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-red-500/20 border border-red-400/30 rounded-full text-red-100 text-sm font-semibold mb-6">
              <MapPin className="w-4 h-4" />
              <span>{t('netherlands.hero.badge')}</span>
            </div>
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              {t('netherlands.hero.title')}
            </h1>
            <p className="text-xl text-red-100 max-w-3xl mx-auto mb-8">
              {t('netherlands.hero.subtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                onClick={scrollToPricing}
                className="bg-white text-red-700 hover:bg-red-50 font-semibold px-8 py-6 text-lg"
              >
                {t('netherlands.hero.getStarted')}
              </Button>
              <Button 
                onClick={scrollToPricing}
                className="bg-red-800 text-white hover:bg-red-900 font-semibold px-8 py-6 text-lg"
              >
                {t('netherlands.hero.viewPricing')}
              </Button>
            </div>
          </div>

          {/* Meppel City Image */}
          <div className="rounded-2xl overflow-hidden max-w-4xl mx-auto">
            <img 
              src="/images/locations/pexels_34926759.jpg" 
              alt="Meppel, Netherlands City View - VPS Hosting Netherlands"
              loading="lazy"
              className="w-full h-96 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <stat.icon className="w-8 h-8 text-red-600" />
                </div>
                <div className="text-4xl font-bold text-gray-900 mb-2">{stat.value}</div>
                <div className="text-gray-600 font-medium">{stat.label}</div>
                {stat.wiki && (
                  <Link
                    to={(language === 'ru' ? '/ru' : '') + '/wiki/' + stat.wiki}
                    className="mt-2 inline-flex items-center gap-1 text-xs text-blue-700 hover:text-blue-900 transition-colors"
                  >
                    {language === 'ru' ? 'Что значит общий 1 Гбит/с?' : 'What does shared 1 Gbps mean?'} →
                  </Link>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Netherlands */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('netherlands.why.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('netherlands.why.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="bg-white rounded-xl p-6 border-2 border-gray-200 hover:border-red-500 transition-colors">
                <div className="w-14 h-14 bg-red-100 rounded-lg flex items-center justify-center mb-4">
                  <benefit.icon className="w-7 h-7 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 leading-relaxed">{benefit.description}</p>
              </div>
            ))}
          </div>

          {/* Datacenter Image */}
          <div className="mt-12 rounded-2xl overflow-hidden">
            <img src="/images/locations/unsplash_1695668548342-c0c1ad479aee.jpg" 
              alt="Datacenter Netherlands - High-Performance VPS Infrastructure" 
              loading="lazy"
              className="w-full h-80 object-cover"
            />
          </div>
        </div>
      </section>

      {/* Technical Features */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">
              {t('netherlands.features.title')}
            </h2>
            <p className="text-xl text-gray-600">
              {t('netherlands.features.subtitle')}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <div key={index} className="text-center">
                <div className="inline-flex items-center justify-center w-16 h-16 bg-red-100 rounded-xl mb-4">
                  <feature.icon className="w-8 h-8 text-red-600" />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                <p className="text-gray-600">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>      {/* Related Locations - Internal Links for SEO */}
      <RelatedLocations currentLocation="vps-netherlands" />



      {/* Pricing Section */}
      <section id="pricing" className="pt-16 pb-12 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              {t('netherlands.pricing.title')}
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto leading-relaxed">
              {t('netherlands.pricing.subtitle')}
            </p>
          </div>
        </div>
        <PricingSection hideHeader={true} />
        
        {/* Payment Methods Section */}
        <div className="pt-16">
          <PaymentMethodsSection transparent={true} />
        </div>
      </section>

      {/* Location Details */}
      <section className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            <div>
              <h2 className="text-4xl font-bold text-gray-900 mb-4">
                {t('netherlands.location.title')}
              </h2>
              <p className="text-lg text-gray-600 mb-6">
                {t('netherlands.location.subtitle')}
              </p>
              <p className="text-gray-700 mb-6 leading-relaxed">
                {t('netherlands.location.about')}
              </p>
              <ul className="space-y-3">
                {t('netherlands.location.benefits').map((benefit, index) => (
                  <li key={index} className="flex items-start gap-2">
                    <CheckCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                    <span className="text-gray-700">{benefit}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="space-y-6">
              <div className="rounded-2xl overflow-hidden">
                <img src="/images/locations/pexels_34953128.jpg" 
                  alt="Netherlands Canal - VPS Hosting Location" 
                  loading="lazy"
                  className="w-full h-64 object-cover"
                />
              </div>
              <div className="rounded-2xl overflow-hidden">
                <img src="/images/locations/unsplash_1608817576203-3c27ed168bd2.jpg" 
                  alt="European Union - Netherlands VPS Hosting" 
                  loading="lazy"
                  className="w-full h-48 object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-gradient-to-r from-red-600 to-red-700">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-white mb-4">
            {t('netherlands.cta.title')}
          </h2>
          <p className="text-xl text-red-100 mb-8">
            {t('netherlands.cta.subtitle')}
          </p>
          <Button 
            onClick={scrollToPricing}
            className="bg-white text-red-700 hover:bg-red-50 font-semibold px-8 py-6 text-lg"
          >
            {t('netherlands.cta.button')}
          </Button>
        </div>
      </section>

      <LocationFAQ location="vps-netherlands" />
      <Footer />
    </div>
  );
};


